"use client";

import React, { useCallback, useEffect, useMemo } from "react";
import {
  useController,
  useFormContext,
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues,
  type PathValue,
} from "react-hook-form";
import {
  BackendTabs,
  BackendTabsContent,
  BackendTabsList,
  BackendTabsTrigger,
  backendTabsListClass,
  backendTabsTriggerClass,
} from "@/app/components/backend/backend-ui";
import ValidationPopover from "@/app/components/ui/validation-popover";
import BackendFormSectionTitle from "@/app/components/backend/backend-form-section-title";
import type {
  ValidationPopoverBgT,
  ValidationErrorT,
} from "@/app/components/ui/validation-popover";
import { cn } from "@/lib/utils";

/**
 * Tab option interface
 */
interface TabOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Props for the ControlledTabs component
 *
 * Standardized interface consistent with Controlled Input Components
 */
interface ControlledTabsProps<TFieldValues extends FieldValues = FieldValues> {
  /** Field name for React Hook Form */
  name: FieldPath<TFieldValues>;
  /** React Hook Form control (optional if used within FormProvider) */
  control?: Control<TFieldValues>;
  /** Callback when field loses focus */
  onFieldBlur?: (fieldName: string) => void;
  /** Callback when tab value changes */
  onChange?: (value: string) => void;

  // Tab-specific props
  /** Array of available tab options */
  options: TabOption[];
  /** Tab content children */
  children: React.ReactNode;
  /** Default tab value */
  defaultValue?: string;
  /** Whether the tabs are read-only (prevents switching) */
  readOnly?: boolean;

  // Styling props
  /** Additional CSS classes for styling */
  className?: string;
  sectionTitle?: string;

  // Validation Popover props
  /** Background variant for Validation Popover */
  bgVariant?: ValidationPopoverBgT;
}

/**
 * ControlledTabs - Consistent with Controlled Input Components
 *
 * Features:
 * - React Hook Form integration with useController
 * - Validation Popover system for consistent error display
 * - Standardized props interface
 * - Consistent event handling (onBlur, onChange)
 * - TypeScript generics for type safety
 * - Form data integration for active tab
 *
 * @example
 * ```tsx
 * <ControlledTabs
 *   name="consumption.type"
 *   options={fuelTypeOptions}
 *   label="Kraftstofftyp"
 *   required
 * >
 *   <ControlledTabsContent value="gasoline">
 *     <GasolineSection />
 *   </ControlledTabsContent>
 * </ControlledTabs>
 * ```
 */
export function ControlledTabs<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  onFieldBlur,
  onChange,
  options,
  children,
  defaultValue,
  readOnly,
  className,
  sectionTitle,
  bgVariant = "muted",
}: ControlledTabsProps<TFieldValues>) {
  const formContext = useFormContext<TFieldValues>();
  const actualControl = control || formContext?.control;

  if (!actualControl) {
    throw new Error(
      "ControlledTabs must be used within a FormProvider or with control prop"
    );
  }

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control: actualControl,
  });

  // ✅ Use useWatch for reactive updates after form.reset()
  const watchedValue = useWatch({
    name,
    control: actualControl,
  });

  const fieldValue = field.value;
  const handleChange = field.onChange;
  const fallbackValue = useMemo(
    () => defaultValue ?? options[0]?.value ?? "",
    [defaultValue, options]
  );

  useEffect(() => {
    if (!fallbackValue) {
      return;
    }

    const currentValue = watchedValue ?? fieldValue;

    if (
      currentValue === undefined ||
      currentValue === null ||
      currentValue === ""
    ) {
      if (formContext) {
        formContext.setValue(
          name,
          fallbackValue as PathValue<TFieldValues, typeof name>,
          {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          }
        );
        return;
      }

      handleChange(fallbackValue);
    }
  }, [
    fieldValue,
    fallbackValue,
    formContext,
    handleChange,
    name,
    watchedValue,
  ]);

  const errorMessage = error?.message;
  const hasError = Boolean(errorMessage);

  // Memoize errorMessages to prevent unnecessary re-renders
  const errorMessages = useMemo(() => {
    return errorMessage ? [errorMessage] : [];
  }, [errorMessage]);

  // Event handlers
  const handleValueChange = useCallback(
    (value: string) => {
      if (readOnly) return;

      field.onChange(value);
      onChange?.(value);
    },
    [field, onChange, readOnly]
  );

  const handleBlur = useCallback(() => {
    field.onBlur();
    onFieldBlur?.(name);
  }, [field, onFieldBlur, name]);

  // ✅ Use watched value for reactive updates, fallback to field.value, then defaultValue
  const currentValue =
    watchedValue || field.value || defaultValue || options[0]?.value || "";

  // Tab styling with error state
  const tabsListStyles = cn(
    backendTabsListClass,
    hasError && "border-destructive"
  );

  const tabsTriggerStyles = cn(
    backendTabsTriggerClass,
    "h-7 flex-none rounded-[8px] px-3 text-xs font-semibold",
    hasError && "text-destructive"
  );

  const getTabsTriggerStyles = (value: string) =>
    cn(
      tabsTriggerStyles,
      currentValue === value &&
        "!border-[var(--c-ink)] !bg-[var(--c-ink)] !text-white !shadow-none"
    );

  const renderTabsList = () => (
    <BackendTabsList className={tabsListStyles}>
      {options.map((option) => (
        <BackendTabsTrigger
          key={option.value}
          value={option.value}
          disabled={option.disabled || readOnly}
          className={getTabsTriggerStyles(option.value)}
        >
          {option.label}
        </BackendTabsTrigger>
      ))}
    </BackendTabsList>
  );

  const renderTabsHeader = () =>
    sectionTitle ? (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <BackendFormSectionTitle title={sectionTitle} />
        {renderTabsList()}
      </div>
    ) : (
      renderTabsList()
    );

  return (
    <div className={className}>
      {hasError && errorMessages.length > 0 ? (
        <ValidationPopover
          error={errorMessages as (string | ValidationErrorT)[]}
          bgVariant={bgVariant}
        >
          <BackendTabs
            value={currentValue}
            onValueChange={handleValueChange}
            onBlur={handleBlur}
          >
            {renderTabsHeader()}
            {children}
          </BackendTabs>
        </ValidationPopover>
      ) : (
        <BackendTabs
          value={currentValue}
          onValueChange={handleValueChange}
          onBlur={handleBlur}
        >
          {renderTabsHeader()}
          {children}
        </BackendTabs>
      )}
    </div>
  );
}

/**
 * ControlledTabsContent - Simple wrapper for TabsContent
 *
 * Wrapper for TabsContent that provides consistent styling.
 * Form integration is handled by the parent ControlledTabs component.
 */
export function ControlledTabsContent({
  value,
  children,
  className,
}: {
  value: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <BackendTabsContent value={value} className={className}>
      {children}
    </BackendTabsContent>
  );
}
